import { Link } from "react-router-dom";
import { useTheme } from "@/components/ThemeContext";
import SEOHead from "@/components/SEOHead";
import { ServiziStructuredData } from "@/components/StructuredData";
import {
  Plane,
  Train,
  PartyPopper,
  Briefcase,
  Map,
  Phone,
  CalendarDays,
  Check,
} from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const FLEET_IMG = "https://images.unsplash.com/photo-1613033686399-aa7c18d6d760?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtZXJjZWRlcyUyMHMlMjBjbGFzcyUyMGRyaXZpbmclMjBuaWdodHxlbnwwfHx8fDE3NzQ1MTc0Mjd8MA&ixlib=rb-4.1.0&q=85";
const STAZIONI_IMG = "https://static.prod-images.emergentagent.com/jobs/7d58b70f-15f1-42b8-9abf-5f4be08ffd33/images/9108fd770fbc384d1cc7cf38a253d92d3b403a67ced31ca6a0ac69068a97fd52.png";
const EVENTI_IMG = "https://static.prod-images.emergentagent.com/jobs/7d58b70f-15f1-42b8-9abf-5f4be08ffd33/images/bdb8f672b52c4cf77d24c1574a88133d562f014a2b426d4c744dd62e16976b05.png";
const BUSINESS_IMG = "https://static.prod-images.emergentagent.com/jobs/7d58b70f-15f1-42b8-9abf-5f4be08ffd33/images/547c51a30f9fe6be1b107c8f2d1ebd151c184d78b4f00f84cd541feceac1a3a7.png";
const TOUR_IMG = "https://static.prod-images.emergentagent.com/jobs/7d58b70f-15f1-42b8-9abf-5f4be08ffd33/images/58832458e9334db11b6befc3af8e3fe3cb613151990d1bb99c9e5637a9c35075.png";

const servizi = [
  {
    icon: Plane,
    title: "Transfer per Aeroporti",
    desc: "Collegamento rapido e puntuale con tutti i principali aeroporti: Napoli Capodichino (NAP), Salerno Pontecagnano (QSR), Roma Fiumicino (FCO), Roma Ciampino (CIA), Bari Palese (BRI) e tutti gli hub italiani ed europei.",
    benefits: [
      "Monitoraggio volo in tempo reale",
      "Accoglienza in aeroporto con cartello",
      "Assistenza bagagli in caso di smarrimento",
      "Attesa in aeroporto in caso di ritardo del volo",
    ],
    image: FLEET_IMG,
  },
  {
    icon: Train,
    title: "Transfer per Stazioni",
    desc: "Servizio di transfer da e per le principali stazioni ferroviarie della Campania e del Sud Italia. Alta Velocità, Intercity e treni regionali.",
    benefits: [
      "Accoglienza in stazione con cartello",
      "Attesa in stazione in caso di ritardi del treno",
      "Acquisto biglietto del treno su richiesta",
      "Cancellazione gratuita fino a 48 ore prima",
    ],
    image: STAZIONI_IMG,
  },
  {
    icon: Briefcase,
    title: "Business Travel",
    desc: "Per chi viaggia per lavoro, il tempo è prezioso. Offriamo un servizio di trasporto executive pensato per i professionisti più esigenti.",
    benefits: [
      "Massima riservatezza",
      "Wi-Fi a bordo",
      "Quotidiano a bordo su richiesta",
      "Convenzioni aziendali con fatturazione mensile",
    ],
    image: BUSINESS_IMG,
  },
  {
    icon: PartyPopper,
    title: "Eventi e Cerimonie",
    desc: "Matrimoni, battesimi, comunioni, feste di compleanno, cene di gala, eventi aziendali e cerimonie importanti meritano un trasporto di eccellenza. Rendiamo ogni momento speciale ancora più elegante.",
    benefits: [
      "Veicoli decorabili su richiesta",
      "Autisti in abito formale",
      "Coordinamento con organizzatori",
      "Servizio fotografico su richiesta",
    ],
    image: EVENTI_IMG,
  },
  {
    icon: Map,
    title: "Tour Personalizzati",
    desc: "Scoprite le meraviglie della Campania con un tour su misura. Costiera Amalfitana, Pompei, Vesuvio, Napoli, Reggia di Caserta, Paestum e molto altro.",
    benefits: [
      "Itinerari personalizzabili",
      "Guida autorizzata in lingua estera su richiesta",
      "Soste fotografiche durante il tour",
      "Tour di intera giornata o mezza giornata",
    ],
    image: TOUR_IMG,
  },
];

export default function ServiziPage() {
  const { isDark } = useTheme();

  return (
    <main data-testid="servizi-page" className="pt-20">
      <SEOHead
        title="Servizi NCC - Transfer Aeroportuali, Stazioni, Eventi, Tour"
        description="Scopri tutti i servizi NCC di Transfer Avellino: transfer aeroportuali Napoli e Roma, transfer stazioni, noleggio con conducente per matrimoni e cerimonie, business travel e tour Costiera Amalfitana, Pompei, Caserta."
        path="/servizi"
      />
      <ServiziStructuredData />
      {/* Hero */}
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-6">
              I Nostri Servizi
            </span>
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl tracking-tight font-normal max-w-3xl leading-[1.1] mb-6 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
              Ogni viaggio è un'esperienza
              <span className="italic text-[#C9A646]"> su misura</span>
            </h1>
            <p className={`text-base md:text-lg max-w-2xl leading-relaxed ${isDark ? "text-[#A1A1AA]" : "text-[#555]"}`}>
              Scoprite la nostra gamma completa di servizi di trasporto premium.
              Qualunque sia la vostra destinazione, vi accompagniamo con stile.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Service Details */}
      {servizi.map((s, i) => {
        const Icon = s.icon;
        const isEven = i % 2 === 0;
        return (
          <section
            key={s.title}
            id={`service-detail-${i}`}
            data-testid={`service-detail-${i}`}
            className={`py-20 md:py-24 transition-colors duration-500 ${
              i % 2 === 0
                ? isDark ? "bg-[#0E0E0E]" : "bg-[#EDE8DC]"
                : isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"
            }`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
                <div className={!isEven ? "lg:order-2" : ""}>
                  <ScrollReveal>
                    <Icon className="w-10 h-10 text-[#C9A646] mb-6" strokeWidth={1.5} />
                    <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-normal mb-4 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
                      {s.title}
                    </h2>
                    <p className={`text-base leading-relaxed mb-8 ${isDark ? "text-[#A1A1AA]" : "text-[#555]"}`}>{s.desc}</p>
                  </ScrollReveal>
                  <ScrollReveal delay={0.1}>
                    <div className="space-y-3 mb-8">
                      {s.benefits.map((b) => (
                        <div key={b} className="flex items-center gap-3">
                          <Check className="w-4 h-4 text-[#C9A646] flex-shrink-0" strokeWidth={2} />
                          <span className={`text-sm ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>{b}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollReveal>
                  <ScrollReveal delay={0.2}>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link
                        to="/prenota"
                        data-testid={`service-book-${i}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
                      >
                        <CalendarDays className="w-4 h-4" strokeWidth={1.5} />
                        Prenota Ora
                      </Link>
                      <a
                        href="tel:+393784035054"
                        data-testid={`service-call-${i}`}
                        className="inline-flex items-center justify-center gap-2 border border-[#C9A646] text-[#C9A646] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#C9A646]/10 transition-all duration-300"
                      >
                        <Phone className="w-4 h-4" strokeWidth={1.5} />
                        Chiama
                      </a>
                    </div>
                  </ScrollReveal>
                </div>

                <ScrollReveal delay={0.15} className={!isEven ? "lg:order-1" : ""}>
                  <div className="relative overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 border border-[#C9A646]/20" />
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>
        );
      })}

      {/* Bottom CTA */}
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0E0E0E]" : "bg-[#1A1A1A]"}`}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-normal text-white mb-4">
              Non trovi il servizio che cerchi?
            </h2>
            <p className="text-[#A1A1AA] mb-10">
              Contattaci per una soluzione personalizzata. Siamo pronti a soddisfare ogni vostra esigenza di trasporto.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <a
              href="https://wa.me/393784035054?text=Salve%2C%20vorrei%20informazioni%20su%20un%20servizio%20personalizzato."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="servizi-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
            >
              Contattaci su WhatsApp
            </a>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
